/**
 * Logger Utility - Centralized logging with environment-aware output
 * Automatically suppresses debug logs in production while keeping errors visible
 */

const isDevelopment = process.env.NODE_ENV === 'development';
const isDebugEnabled = process.env.REACT_APP_DEBUG === 'true';

// Log levels
const LOG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3
};

// Current log level (in production, only show warnings and errors)
const currentLevel = isDevelopment || isDebugEnabled ? LOG_LEVELS.DEBUG : LOG_LEVELS.WARN;

/**
 * Format log message with timestamp and prefix
 */
const formatMessage = (level, prefix, message) => {
  const timestamp = new Date().toISOString().slice(11, 23);
  return `[${timestamp}] [${level}]${prefix ? ` [${prefix}]` : ''} ${message}`;
};

/**
 * Logger object with level-specific methods
 */
const logger = {
  /**
   * Debug - Only shown in development
   */
  debug: (message, ...args) => {
    if (currentLevel <= LOG_LEVELS.DEBUG) {
      console.log(formatMessage('DEBUG', '', message), ...args);
    }
  },

  /**
   * Info - Only shown in development
   */
  info: (message, ...args) => {
    if (currentLevel <= LOG_LEVELS.INFO) {
      console.info(formatMessage('INFO', '', message), ...args);
    }
  },

  /**
   * Warn - Shown in all environments
   */
  warn: (message, ...args) => {
    if (currentLevel <= LOG_LEVELS.WARN) {
      console.warn(formatMessage('WARN', '', message), ...args);
    }
  },

  /**
   * Error - Always shown
   */
  error: (message, ...args) => {
    console.error(formatMessage('ERROR', '', message), ...args);
  },

  /**
   * Create a namespaced logger for a specific module
   */
  create: (namespace) => ({
    debug: (message, ...args) => {
      if (currentLevel <= LOG_LEVELS.DEBUG) {
        console.log(formatMessage('DEBUG', namespace, message), ...args);
      }
    },
    info: (message, ...args) => {
      if (currentLevel <= LOG_LEVELS.INFO) {
        console.info(formatMessage('INFO', namespace, message), ...args);
      }
    },
    warn: (message, ...args) => {
      if (currentLevel <= LOG_LEVELS.WARN) {
        console.warn(formatMessage('WARN', namespace, message), ...args);
      }
    },
    error: (message, ...args) => {
      console.error(formatMessage('ERROR', namespace, message), ...args);
    }
  }),

  /**
   * Group related logs
   */
  group: (label) => {
    if (currentLevel <= LOG_LEVELS.DEBUG) {
      console.group(label);
    }
  },

  /**
   * End log group
   */
  groupEnd: () => {
    if (currentLevel <= LOG_LEVELS.DEBUG) {
      console.groupEnd();
    }
  },

  /**
   * Log timing
   */
  time: (label) => {
    if (currentLevel <= LOG_LEVELS.DEBUG) {
      console.time(label);
    }
  },

  /**
   * End timing
   */
  timeEnd: (label) => {
    if (currentLevel <= LOG_LEVELS.DEBUG) {
      console.timeEnd(label);
    }
  },

  /**
   * Log a table
   */
  table: (data) => {
    if (currentLevel <= LOG_LEVELS.DEBUG) {
      console.table(data);
    }
  },

  /**
   * Assert a condition
   */
  assert: (condition, message, ...args) => {
    if (!condition) {
      console.error(formatMessage('ASSERT', '', message), ...args);
    }
  }
};

export default logger;
export { logger, LOG_LEVELS };
